import { forwardRef } from 'react';
import { Container, ContainerProps } from './container';
import { pageContainerWithMarginStyles } from './commonStyles';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Props for the CenteredPageContainer component.
 * Extends ContainerProps with all props available.
 */
export type CenteredPageContainerProps = ContainerProps;

/**
 * CenteredPageContainer - Standalone page container with vertical margin.
 *
 * A pre-configured Container for standalone pages that need vertical separation
 * from the viewport top. Uses a 10vh top margin to center content vertically
 * on the page.
 *
 * **When to use:**
 * - No tenant/error state pages
 * - Standalone informational pages
 * - Login/authentication pages
 * - Any page that should be vertically centered
 *
 * **Pattern from:** NoTenantPage inline styles
 *
 * @example
 * ```tsx
 * import { CenteredPageContainer } from '@planitgeo/treeplotter-ui-library';
 *
 * function NoTenantPage() {
 *   return (
 *     <CenteredPageContainer maxWidth="xl">
 *       <Typography variant="h4">No Tenant Found</Typography>
 *       <Typography>Please contact your administrator.</Typography>
 *       <Button>Sign Out</Button>
 *     </CenteredPageContainer>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With custom maxWidth
 * <CenteredPageContainer maxWidth="md">
 *   <LoginForm />
 * </CenteredPageContainer>
 * ```
 *
 * @example
 * ```tsx
 * // With additional sx styles
 * <CenteredPageContainer maxWidth="lg" sx={{ textAlign: 'center' }}>
 *   <ErrorContent />
 * </CenteredPageContainer>
 * ```
 */
export const CenteredPageContainer = forwardRef<HTMLDivElement, CenteredPageContainerProps>(
  ({ sx, ...props }, ref) => {
    // Merge the base styles with any custom sx props
    const mergedSx: SxProps<Theme> = sx
      ? [pageContainerWithMarginStyles, sx].flat()
      : pageContainerWithMarginStyles;

    return <Container ref={ref} sx={mergedSx} {...props} />;
  }
);

CenteredPageContainer.displayName = 'CenteredPageContainer';
